import React, { useCallback } from 'react';
import { devWarning } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion';
import { CloseIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { css } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';
import { useTranslation } from './localization';
import { PillBase, PillBaseProps } from './PillBase';
import { TooltipContent } from './TooltipContent';

const StyledIconButton = styled(IconButton)({
  display: 'block',
});

export interface Props
  extends Pick<
    PillBaseProps,
    | 'keyForValue'
    | 'leadingIcon'
    | 'maxWidth'
    | 'customTooltipContent'
    | 'children'
    | 'size'
    | 'disabled'
  > {
  /**
   * Handler for when a Pill's "remove" button is clicked.
   * If no `onRemove` is desired use the Label component instead.
   */
  onRemove?: () => void;

  /**
   * Used for the "remove" button's `aria-label` attribute. Defaults to "Remove".
   */
  removeLabel?: string;
}

const RemoveButtonContainer = styled('span')(
  css({
    paddingLeft: ThemeSpace.minor[1],
  }),
);

/**
 * A `Pill` is a capsule-shaped UI element that contains either plain text or custom UI content.
 * It is typically used to display a small list of items where you can remove items from a list.
 *
 * The coreui `Pill` and
 * [`Label`](https://pages.git.corp.tanium.com/eng/ufe/?path=/story/core-ui-components-label--overview)
 * components are similar in appearance and props.
 *
 * Which component should I use?
 * - If you want to allow users to remove items from the list (e.g. filtering or multi-select),
 *   use the `Pill` component which has an `onRemove` property.
 * - If you need to display a read-only list (e.g. a set of tags associated with an item),
 *   use the `Label` component which does not support any user interaction.
 */
export const Pill = React.memo(
  ({
    children,
    customTooltipContent,
    keyForValue,
    leadingIcon,
    maxWidth,
    onRemove,
    removeLabel,
    size = 'lg',
    disabled = false,
  }: Props) => {
    devWarning(
      !!onRemove,
      'Using a Pill component without an onRemove prop has been deprecated. If no onRemove is desired use the Label component.',
    );

    const { t } = useTranslation();
    const removeButtonHeight = size === 'lg' ? 14 : 12; // icon is slightly smaller than button, this gets icon close to UX spec size

    const handleRemove = useCallback<React.MouseEventHandler<HTMLElement>>(
      (e) => {
        onRemove?.();
        e.stopPropagation();
      },
      [onRemove],
    );

    return (
      <PillBase
        {...{
          keyForValue,
          leadingIcon,
          maxWidth,
          onRemove,
          removeLabel,
          alwaysEnableTooltip: !!customTooltipContent,
          size,
          disabled,
        }}
        tooltipContent={
          customTooltipContent ?? (
            <TooltipContent keyForValue={keyForValue}>{children}</TooltipContent>
          )
        }
        trailingContent={
          onRemove && (
            <RemoveButtonContainer>
              <StyledIconButton
                icon={CloseIcon}
                height={removeButtonHeight}
                width={removeButtonHeight}
                label={removeLabel || t('Remove')}
                onClick={handleRemove}
                disabled={disabled}
              />
            </RemoveButtonContainer>
          )
        }
      >
        <Box as="span" fontWeight="bold">
          {children}
        </Box>
      </PillBase>
    );
  },
);
